import styled, { useTheme } from 'styled-components'
import { useRef, useState, useEffect, ReactElement } from 'react'
import { useGetSize } from 'Hooks/useGetSize'
import * as d3 from 'd3'
import { AnimatedNote } from '../ExampleComponent/AnimatedNote'
import { useMediaQuery } from 'styled-breakpoints/use-media-query'
import { Note } from 'theme/styles'
import { useSpring, animated } from '@react-spring/web'

const AnimatedPath = ({
  data,
  Type,
}: {
  data: [number, number][]
  Type: any
}) => {
  const pathRef = useRef<SVGPathElement>(null)
  const [newData, setNewData] = useState<number[]>([])

  useEffect(() => {
    // flatten the coordinates to get an interpolable array
    const d = data.flatMap((subArray) => subArray)
    setNewData(d)
  }, [data])

  const spring = useSpring({
    data: newData,
    onChange: (data) => {
      // Reconstruction of the 2D array
      const groupSize = 2
      const interpolatedArray = []
      const d = data.value.data
      for (let i = 0; i < d.length; i += groupSize) {
        interpolatedArray.push(d.slice(i, i + groupSize))
      }

      // Directly update the path
      if (pathRef.current) {
        const lineGenerator = d3.line()
        const newPathD = lineGenerator(interpolatedArray)
        pathRef.current.setAttribute('d', newPathD ?? '')
      }
    },
  })

  return <Type ref={pathRef} />
}

const PcChart = ({ data, step }: { data: WomenData[]; step: number }) => {
  //const { breakpoints } = useTheme()
  //const isMobile = useMediaQuery(breakpoints.only('md'))

  const isMobile = window.innerWidth < 768
  const containerRef = useRef<HTMLDivElement>(null)
  const svgRef = useRef<SVGSVGElement>(null)
  const { width } = useGetSize(containerRef)
  const height = isMobile ? width * 1.65 : width * 0.75

  const maxX = d3.max(data, (d) => d.age) ?? 0
  const maxY = d3.max(data, (d) => d.women) ?? 0

  const xDomain = [64, maxX]
  const yDomain = [0, 45]
  const margin = {
    top: 30,
    right: 10,
    bottom: 30,
    left: 80,
  }
  const tickYLength = 18
  const tickXLength = 12

  const xScale = d3
    .scaleLinear()
    .domain(xDomain)
    .range([margin.left, width - margin.right])

  const yScale = d3
    .scaleLinear()
    .domain(yDomain)
    .range([height - margin.bottom, margin.top])

  const lineGenerator = d3.line() //.curve(d3.curveCatmullRom.alpha(0.5))

  const menDataLine: [number, number][] = data.map((d) => {
    return [xScale(d.age), yScale(d.men)]
  })
  const menDataHighLine: [number, number][] = data.map((d) => {
    return [xScale(d.age), yScale(d.men + 11)]
  })
  const womenDataLine: [number, number][] = data.map((d) => {
    return [xScale(d.age), yScale(d.women)]
  })
  const womenDataHighLine: [number, number][] = data.map((d) => {
    return [xScale(d.age), yScale(d.women + 19.9)]
  })
  const totalDataLine: [number, number][] = data.map((d) => {
    return [xScale(d.age), yScale(d.total)]
  })

  const ageDataArea = [...menDataLine, ...[...womenDataLine].reverse()]
  const ageDataHighArea = [
    ...menDataHighLine,
    ...[...womenDataHighLine].reverse(),
  ]

  const ageLinePath = lineGenerator(menDataLine) || undefined
  const menLineHighPath = lineGenerator(menDataHighLine) || undefined
  const womenLinePath = lineGenerator(womenDataLine) || undefined
  const womenLineHighPath = lineGenerator(womenDataHighLine) || undefined

  const ageAreaHighPath = lineGenerator(ageDataArea) || undefined

  const xTicks = isMobile ? [65, 80, 95] : [65, 70, 75, 80, 85, 90, 95]
  const yTicks = [10, 20, 30, 40]

  return (
    <Container ref={containerRef}>
      <Svg ref={svgRef} width={width} height={height}>

        {/* Y grid */}
        {/* {xTicks.map((value) => (
          <GridLine
            x1={xScale(value)}
            x2={xScale(value)}
            y1={margin.top}
            y2={height - margin.bottom}
          />
        ))} */}

        {/* X ticks */}
        {xTicks.map((value) => (
          <Tick
            x1={xScale(value)}
            x2={xScale(value)}
            y1={height - margin.bottom + tickXLength}
            y2={height - margin.bottom}
          />
        ))}

        {/* X grid */}
        {yTicks.map((value) => {
          return (
            <GridLine
              x1={0}
              x2={width - margin.right}
              y1={yScale(value)}
              y2={yScale(value)}
            />
          )
        })}

        {/* Y ticks */}
        {/* {yTicks.map((value) => {
          return (
            <Tick
              x1={margin.left - tickYLength - 20}
              x2={margin.left - 20}
              y1={yScale(value)}
              y2={yScale(value)}
            />
          )
        })} */}

        {/* <Area highlight={step > 1} d={ageAreaHighPath} /> */}
        <AgePathLineMen d={ageLinePath} />
        <AgePathLine d={womenLinePath} />

        {/* High area */}
        <AnimatedPath
          data={ageDataArea}
          Type={HighArea}
        />
        {/* <AnimatedPath
          data={step > 1 ? womenDataHighLine : womenDataLine}
          Type={AgePathLine}
        />
        <AnimatedPath
          data={step > 1 ? menDataHighLine : menDataLine}
          Type={AgePathLineMen}
        /> */}

        <BottomLine
          x1={0}
          x2={width - margin.right}
          y1={height - margin.bottom}
          y2={height - margin.bottom}
        />

<Marker
        style={{
          opacity: step === 2 ? 1 : 0,
        }}
          x1={xScale(80)}
          x2={xScale(80)}
          y1={margin.top}
          y2={height - margin.bottom}
        />


        <BabyBoomerArea
        style={{
          opacity: step === 2 ? 1 : 0,
        }}
          x={xScale(80)}
          y={margin.top}
          width={width - xScale(80) - margin.right}
          height={height - margin.bottom - margin.top}
        />
      </Svg>

      {/* Y labels */}
      {yTicks.map((value) => (
        <Label
          style={{
            top: yScale(value) - 28,
            left: 0,
            transform: 'translateX(0)',
            textAlign: 'right',
            width: 50,
          }}
        >
          {value.toLocaleString('fr-CH')}%
        </Label>
      ))}

      <Label
        style={{
          top: yScale(150000) - 48,
          left: 0,
          transform: 'translateX(0)',
          textAlign: 'left',
          letterSpacing: 0.85,
          width: 150,
        }}
      >
        <strong>Population</strong>
      </Label>

      {/* X labels */}
      {xTicks.map((value) => (
        <Label style={{ left: xScale(value), top: height }}>
          {value === 0 ? `0` : `${value} ans`}
        </Label>
      ))}

      {/* Line on 2024 for reference */}
      <NoteWomen
        style={{
          top: yScale(15),
          left: xScale(70),
          position: 'absolute',
        }}
      >
        Femmes
      </NoteWomen>

      <AnimatedNote show={step < 2} position={[yScale(25), xScale(93)]}>
        Écart entre les hommes et femmes bénéficiaires des <strong>prestations complémentaires</strong>
      </AnimatedNote>

      <NoteMen
        style={{
          top: yScale(8),
          left: xScale(70),
          position: 'absolute',
          fontWeight: 'bold'
        }}
      >
        Hommes
      </NoteMen>

      <Label
        style={{
          top: 0,
          left: 0,
          transform: 'translateX(0)',
          textAlign: 'left',
          letterSpacing: 0.85,
          width: 250,
        }}
      >
        <strong>Taux de bénéficiaires de prestations complémentaires à l'AVS</strong>
      </Label>

      {/* <MainNote
        style={{
          top: yScale(50000),
          left: xScale(30),
        }}
      >
        Répartition de la population <strong>en {year}</strong>
      </MainNote> */}
    </Container>
  )
}

const Container = styled.div`
  width: 100%;
  position: relative;
`

const AgePathLine = styled.path`
  stroke: ${({ theme }) => `rgb(${theme.mainColor})`};
  stroke-width: 3px;
  fill: none;
`

const AgePathLineMen = styled(AgePathLine)`
  stroke: ${({ theme }) => theme.textColor};
`

const Line = styled.path`
  stroke-width: 3px;
  stroke: #ffe462;
  fill: none;
`

const Area = styled.path<{ highlight: boolean }>`
  stroke: none;
  fill: ${({ theme, highlight }) =>
    `rgb(200, 200, 200, ${!highlight ? 0 : 0.5})`};
  transition: fill 0.3s;
`

const HighArea = styled(Area)`
  fill: ${({ theme }) => `rgb(${theme.mainColor}, 0.65)`};
`

const OriginalPath = styled(Area)`
  stroke: none;
  fill: #22335511;
`

const GridLine = styled.line`
  stroke: #eaebef;
  stroke-width: 1px;
`

const Tick = styled.line`
  stroke: var(--text-color);
  stroke-width: 1.25px;
`

const Marker = styled.line`
  stroke: var(--text-color);
  stroke-width: 2px;
  stroke-dasharray: 2px 5px;
  stroke-linecap: round;
  transition: opacity 0.3s ease;
`

const MainNote = styled.div`
  width: 150px;
  letter-spacing: 0.05em;
  line-height: 1.2;
  text-align: left;
  position: absolute;
  // text-shadow:
  //   1px 1px 0 #ffffff88,
  //   -1px 1px 0 #ffffff88,
  //   -1px -1px 0 #ffffff88,
  //   1px -1px 0 #ffffff88;
`

const MarkerArea = styled.rect`
  fill: #202346;
  opacity: 0.05;
  stroke: none;
`

const BabyBoomerArea = styled.rect`
  fill: ${({ theme }) => `rgb(${theme.mainColor}, 0.2)`};
  stroke: none;
  mix-blend-mode: multiply;
  transition: opacity 0.3s ease;
`

const BottomLine = styled.line`
  stroke: var(--text-color);
  stroke-width: 1.25px;
`

const Label = styled.div`
position: absolute;
left: 0;
top: 0;
color: var(--text-color);
transform: translate(-50%, -50%);
font-size: 0.8em;
line-height: 1.1;
`

const NoteWomen = styled(Note)`
color: ${({ theme }) => `rgb(${theme.mainColor})`};
font-weight: bold;
`

const NoteMen = styled(Note)`
  color: ${({ theme }) => theme.textColor};
  font-weight: bold;
`

const Svg = styled.svg``

export default PcChart
